import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import dashboardService from '../../services/dashboardService'

// ── Thunks — all accept filters and pass them to the service ──────────────────

export const fetchDashboardKpis = createAsyncThunk('dashboard/fetchKpis',
  async (filters, { rejectWithValue }) => {
    try { return await dashboardService.getKpis(filters) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchHourlyVolume = createAsyncThunk('dashboard/fetchHourlyVolume',
  async (filters, { rejectWithValue }) => {
    try { return await dashboardService.getHourlyVolume(filters) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchApprovalBlockTrend = createAsyncThunk('dashboard/fetchApprovalBlockTrend',
  async (filters, { rejectWithValue }) => {
    try { return await dashboardService.getApprovalBlockTrend(filters) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchAlertTrend = createAsyncThunk('dashboard/fetchAlertTrend',
  async (filters, { rejectWithValue }) => {
    try { return await dashboardService.getAlertTrend(filters) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchTopRules = createAsyncThunk('dashboard/fetchTopRules',
  async (filters, { rejectWithValue }) => {
    try { return await dashboardService.getTopRules(filters) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchTopRiskyMerchants = createAsyncThunk('dashboard/fetchTopRiskyMerchants',
  async (filters, { rejectWithValue }) => {
    try { return await dashboardService.getTopRiskyMerchants(filters) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchRecentFlagged = createAsyncThunk('dashboard/fetchRecentFlagged',
  async (filters, { rejectWithValue }) => {
    try { return await dashboardService.getRecentFlagged(filters) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

export const fetchLatencyHealth = createAsyncThunk('dashboard/fetchLatencyHealth',
  async (filters, { rejectWithValue }) => {
    try { return await dashboardService.getLatencyHealth(filters) }
    catch (e) { return rejectWithValue(e.message) }
  }
)

// ── Master refresh — dispatches ALL widgets with current filters ───────────────
// BUG 1 FIX: date range filter now propagates to every API call
export const refreshDashboard = createAsyncThunk(
  'dashboard/refreshAll',
  async (filters, { dispatch, getState }) => {

    const currentFilters =
      filters || getState().dashboard.filters

    await Promise.all([
      dispatch(fetchDashboardKpis(currentFilters)),
      dispatch(fetchHourlyVolume(currentFilters)),
      dispatch(fetchApprovalBlockTrend(currentFilters)),
      dispatch(fetchAlertTrend(currentFilters)),
      dispatch(fetchTopRules(currentFilters)),
      dispatch(fetchTopRiskyMerchants(currentFilters)),
      dispatch(fetchRecentFlagged(currentFilters)),
      dispatch(fetchLatencyHealth(currentFilters)),
    ])
  }
)

// ── Slice ──────────────────────────────────────────────────────────────────────
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    kpis:                    null,
    hourlyVolume:            [],
    approvalBlockTrend:      [],
    approvalBlockGranularity:'day',
    alertTrend:              [],
    alertTrendGranularity:   'day',
    topRules:           [],
    riskyMerchants:     [],
    recentFlagged:      [],
    latencyHealth:      null,
    loading: {
      kpis: false, hourly: false, trend: false, alertTrend: false,
      rules: false, merchants: false, flagged: false, latency: false,
    },
    errors: {},
    lastRefresh: null,
    filters: {
      dateRange: 'today',
      merchant:  '',
      channel:   '',
      status:    '',
    },
  },
  reducers: {
    setDashboardFilter(state, { payload }) {
      state.filters = { ...state.filters, ...payload }
      // NOTE: after dispatching this, call refreshDashboard() to re-fetch with new filters
    },
    resetDashboardFilters(state) {
      state.filters = { dateRange: 'today', merchant: '', channel: '', status: '' }
    },
  },
  extraReducers: (builder) => {
    const load = (key) => ({
      pending:  (s)    => { s.loading[key] = true;  s.errors[key] = null },
      rejected: (s, a) => { s.loading[key] = false; s.errors[key] = a.payload },
    })

    builder
      .addCase(fetchDashboardKpis.pending,   load('kpis').pending)
      .addCase(fetchDashboardKpis.rejected,  load('kpis').rejected)
      .addCase(fetchDashboardKpis.fulfilled, (s, a) => {
        s.loading.kpis = false
        s.kpis = a.payload
        s.lastRefresh = Date.now()
      })

      .addCase(fetchHourlyVolume.pending,    load('hourly').pending)
      .addCase(fetchHourlyVolume.rejected,   load('hourly').rejected)
      .addCase(fetchHourlyVolume.fulfilled,  (s, a) => {
        s.loading.hourly = false
        s.hourlyVolume = a.payload?.data ?? a.payload ?? []
      })

      .addCase(fetchApprovalBlockTrend.pending, (s) => {
        s.loading.trend = true
        s.errors.trend  = null
        s.approvalBlockTrend = []   // clear stale data so chart re-renders fresh
      })
      .addCase(fetchApprovalBlockTrend.rejected, (s, a) => {
        s.loading.trend = false
        s.errors.trend  = a.payload
      })
      .addCase(fetchApprovalBlockTrend.fulfilled, (s, a) => {
        s.loading.trend              = false
        s.approvalBlockTrend         = a.payload?.data ?? a.payload ?? []
        s.approvalBlockGranularity   = a.payload?.granularity ?? 'day'
      })

      .addCase(fetchAlertTrend.pending, (s) => {
        s.loading.alertTrend = true
        s.errors.alertTrend  = null
      })
      .addCase(fetchAlertTrend.rejected, (s, a) => {
        s.loading.alertTrend = false
        s.errors.alertTrend  = a.payload
      })
      .addCase(fetchAlertTrend.fulfilled, (s, a) => {
        s.loading.alertTrend      = false
        s.alertTrend              = a.payload?.data ?? a.payload ?? []
        s.alertTrendGranularity   = a.payload?.granularity ?? 'day'
      })

      // BUG 3 FIX: topRules — handle both {data:[]} and direct array responses
      .addCase(fetchTopRules.pending,   load('rules').pending)
      .addCase(fetchTopRules.rejected,  load('rules').rejected)
      .addCase(fetchTopRules.fulfilled, (s, a) => {
        s.loading.rules = false
        s.topRules = a.payload?.data ?? a.payload ?? []
      })

      .addCase(fetchTopRiskyMerchants.pending,   load('merchants').pending)
      .addCase(fetchTopRiskyMerchants.rejected,  load('merchants').rejected)
      .addCase(fetchTopRiskyMerchants.fulfilled, (s, a) => {
        s.loading.merchants = false
        s.riskyMerchants = a.payload?.data ?? a.payload ?? []
      })

      .addCase(fetchRecentFlagged.pending,   load('flagged').pending)
      .addCase(fetchRecentFlagged.rejected,  load('flagged').rejected)
      .addCase(fetchRecentFlagged.fulfilled, (s, a) => {
        s.loading.flagged = false
        s.recentFlagged = a.payload?.data ?? a.payload ?? []
      })

      // BUG 2 FIX: latencyHealth — handle null/empty response gracefully
      .addCase(fetchLatencyHealth.pending,   load('latency').pending)
      .addCase(fetchLatencyHealth.rejected,  load('latency').rejected)
      .addCase(fetchLatencyHealth.fulfilled, (s, a) => {
        s.loading.latency = false
        s.latencyHealth = a.payload ?? null
      })
  },
})

export const { setDashboardFilter, resetDashboardFilters } = dashboardSlice.actions

// Selectors
export const selectKpis             = s => s.dashboard.kpis
export const selectHourlyVolume     = s => s.dashboard.hourlyVolume
export const selectApprovalTrend    = s => s.dashboard.approvalBlockTrend
export const selectAlertTrend       = s => s.dashboard.alertTrend
export const selectApprovalGranularity = s => s.dashboard.approvalBlockGranularity
export const selectAlertGranularity    = s => s.dashboard.alertTrendGranularity
export const selectTopRules            = s => s.dashboard.topRules
export const selectRiskyMerchants   = s => s.dashboard.riskyMerchants
export const selectRecentFlagged    = s => s.dashboard.recentFlagged
export const selectLatencyHealth    = s => s.dashboard.latencyHealth
export const selectDashboardLoading = s => s.dashboard.loading
export const selectDashboardFilters = s => s.dashboard.filters
export const selectLastRefresh      = s => s.dashboard.lastRefresh

export default dashboardSlice.reducer
